import {
  Table,
  Dialog,
  Button,
  Divider,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
} from '@mui/material';
import { TransactionStatus } from 'src/type/transaction';
import Label from 'src/components/label';
import { fNumber } from 'src/utils/format-number';
import { fDate } from 'src/utils/format-time';
import InfoGrid from 'src/components/ui-kit/info-grid';
import { RedeemTxType } from 'src/type/gold-withdraw';
import ImageWithPreview from 'src/components/ui-kit/image-with-preview';
import { txStatusLabel } from '../__gold-withdraw/utils';
import { statusAction } from './utils';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  tx: RedeemTxType;
};

export default function RedeemViewDialog({ open, onClose, tx }: Props) {
  const getInfo = () => {
    if (!tx) return [];

    const {
      status,
      createdAt,
      updatedAt,
      user,
      branch,
      product,
      note,
      redeemImageUrl,
      approvedByUser,
      newAppointmentDate,
      redeemReceiptNo,
      pricePerUnit,
    } = tx;

    return [
      {
        label: 'สถานะ',
        value: (
          <Label
            variant="soft"
            color={
              (status === TransactionStatus.SUCCESS && 'success') ||
              (status === TransactionStatus.PENDING && 'warning') ||
              (status === TransactionStatus.REJECTED && 'error') ||
              (status === TransactionStatus.CANCEL && 'info') ||
              (status === TransactionStatus.WAITING_TICKET && 'warning') ||
              'default'
            }
          >
            {txStatusLabel(status)}
          </Label>
        ),
      },
      { label: 'ผู้ใช้งาน', value: `${user?.firstName} ${user?.lastName}` },
      { label: 'สินค้า', value: product.name },
      {
        label: 'ราคาต่อหน่วย (บาท)',
        value:
          pricePerUnit === 0 || pricePerUnit === null || pricePerUnit === undefined
            ? 'รอเลือกตั๋วทอง'
            : `${fNumber(pricePerUnit || 0)} THB`,
      },
      { label: 'สาขา', value: branch.name },
      { label: 'วันที่ทำรายการ', value: fDate(createdAt, 'DD MMMM BBBB HH:mm น.') },
      {
        label: 'วันที่นัดหมาย (เดิม)',
        value: fDate(tx.appointmentDate, 'DD MMMM BBBB HH:mm น.') || '-',
      },
      {
        label: 'วันที่นัดหมาย (ใหม่)',
        value: fDate(newAppointmentDate, 'DD MMMM BBBB HH:mm น.') || '-',
      },
      ...([TransactionStatus.SUCCESS, TransactionStatus.REJECTED].includes(status)
        ? [
            {
              label: `วันที่${statusAction(status)}`,
              value: fDate(updatedAt, 'DD MMMM BBBB HH:mm น.'),
            },
          ]
        : []),

      ...(approvedByUser
        ? [
            {
              label: `${statusAction(status)}โดย`,
              value: `${approvedByUser?.firstName} ${approvedByUser?.lastName}`,
            },
          ]
        : []),

      ...(redeemImageUrl
        ? [
            {
              label: 'รูปหลักฐาน',
              value: <ImageWithPreview src={redeemImageUrl} alt="" />,
            },
          ]
        : []),
      ...(redeemReceiptNo
        ? [
            {
              label: 'เลขที่ใบเสร็จ',
              value: redeemReceiptNo,
            },
          ]
        : []),
      {
        label: 'หมายเหตุ',
        value: note || '-',
      },
    ];
  };

  // console.log(tx?.productRedeemFees);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle sx={{ pb: 2, display: 'flex' }}>
        ถอนสินค้า
        {tx.product.name} {tx.amount} {tx.unitName || 'บาท'}
      </DialogTitle>

      <DialogContent>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell align="center">จำนวน</TableCell>
                <TableCell align="center">ค่าธรรมเนียม</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tx?.productRedeemFees?.map((fee) => (
                <TableRow key={fee.id}>
                  <TableCell>{fee?.productRedeemFee?.name}</TableCell>
                  <TableCell align="center">{fNumber(fee?.amount || 0)}</TableCell>
                  <TableCell align="center">{fNumber(fee?.feeTotal || 0)} THB</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Divider style={{ borderStyle: 'dashed' }} />

          <Table size="small" sx={{ mt: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>น้ำหนักทองทั้งหมด</TableCell>
                <TableCell align="center">
                  {fNumber(tx?.amount || 0)} {tx.unitName || 'บาท'}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>ค่าธรรมเนียมทั้งหมด</TableCell>
                <TableCell align="center">{fNumber(tx?.fee || 0)} THB</TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
        <InfoGrid infos={getInfo()} />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
}
